import { Localized } from "@fluent/react";
import {
    Alert,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    FormControlLabel,
    Checkbox,
    Typography,
    Skeleton,
    Autocomplete,
    Avatar,
} from "@mui/material";
import { useState, useMemo, useEffect, ChangeEvent } from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { AppId } from "../../../../app/appTypes";
import { showError, showSuccess } from "../../../notifications/notificationsSlice";
import { CancelButton } from "../../../../app/components/CancelButton";
import { GenerateButton } from "../../../../app/components/GenerateButton";
import { generateReportForProtocol, ProcessDocumentOverview, ProcessStageOverview } from "../validationProcessApi";
import { selectAllUsers, User } from "../../../users/usersSlice";
import { LoadUsers } from "../../../../app/AppDataLoader";
import PersonIcon from "@mui/icons-material/Person";
import { RequiredError } from "../../../../app/components/RequiredError";
import { DocumentNumberTextField } from "../../../documents/controls/DocumentNumberTextField";
import { buildReportNumber, buildReportTitle } from "../utils/documentNumberingHelper";



export interface GenerareReportForProtocolProps {
    processId: AppId;
    stageId: string;
    documentId: number;
    protocolDocument: ProcessDocumentOverview;
    onSuccessfullGenerated: (stage: ProcessStageOverview) => void;
    onCancel: () => void;
}


export const GenerareReportForProtocol = ({
    processId,
    stageId,
    documentId,
    protocolDocument,
    onSuccessfullGenerated,
    onCancel,
}: GenerareReportForProtocolProps) => {
    const dispatch = useAppDispatch();
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState("");
    const [documentNumber, setDocumentNumber] = useState<string>("");
    const [documentTitle, setDocumentTitle] = useState<string>("");
    const [responsiblePerson, setResponsiblePerson] = useState<User | null>(null);
    const [responsibleDirty, setResponsibleDirty] = useState(false);
    const isResponsibleOk = !!(responsiblePerson);

    const [copyTeamFromProtocol, setCopyTeamFromProtocol] = useState<boolean>(true);
    const allUsers = useAppSelector(selectAllUsers);

    const isValid = useMemo(() => {
        if (!documentNumber?.trim()) return false;
        if (!documentTitle?.trim()) return false;
        if (!copyTeamFromProtocol && !responsiblePerson) return false;
        return true;
    }, [documentNumber, documentTitle, copyTeamFromProtocol, responsiblePerson]);

    useEffect(() => {
        if (!documentNumber) setDocumentNumber(buildReportNumber(protocolDocument?.no));
        if (!documentTitle) setDocumentTitle(buildReportTitle(protocolDocument?.title));
    }, [protocolDocument]);

    const handleDocumentNumberChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setDocumentNumber(e.target.value);
    };

    const handleConfirmGenerateClick = async () => {
        setSaving(true);
        try {
            if (processId) {
                const result = await dispatch(
                    generateReportForProtocol({
                        processId,
                        stageId,
                        documentId,
                        documentNumber,
                        documentTitle,
                        copyTeamFromProtocol,
                        responsiblePersonName: copyTeamFromProtocol ? null : responsiblePerson?.userName,
                    } as any)
                );

                if (generateReportForProtocol.fulfilled.match(result)) {
                    const stage = result.payload as ProcessStageOverview;
                    dispatch(showSuccess("saved"));
                    onSuccessfullGenerated(stage);
                } else {
                    let errorMsg = "error"
                    if (result.payload && result.payload.kind === 'http') {
                        const problem = result.payload.problem
                        if (problem) {
                            errorMsg = problem.title
                        }
                    }
                    setServerError(errorMsg)
                }
            }
        } catch {
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };


    return (
        <Dialog
            open
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    minWidth: { xs: "auto", sm: 300 },
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    bgcolor: "grey.200",
                    color: "text.primary",
                    fontWeight: "bold",
                }}
            >
                <Localized id="process-generate-confirmation">
                    <span>Czy wygenerować raport na podstawie protokołu?</span>
                </Localized>
            </DialogTitle>

            <DialogContent dividers>
                <Box
                    sx={(t) => ({
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2,
                        p: 2,
                        mb: 2,
                        border: `1px solid ${t.palette.divider}`,
                        borderRadius: 1.5,
                        bgcolor: t.palette.action.hover,
                    })}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                            Raport zostanie wygenerowany dla dokumentu:
                        </Typography>

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "auto 1fr",
                                rowGap: 0.5,
                                columnGap: 1,
                            }}
                        >
                            {/* Typ dokumentu */}
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                            >
                                <Box component="span" sx={{ fontWeight: 600 }}>
                                    Typ dokumentu:
                                </Box>
                            </Typography>
                            <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                                {protocolDocument?.type || "-"}
                            </Typography>

                            {/* Numer */}
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                            >
                                <Box component="span" sx={{ fontWeight: 600 }}>
                                    Numer:
                                </Box>
                            </Typography>
                            <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                                {protocolDocument.no || "-"}
                            </Typography>

                            {/* Tytuł */}
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                            >
                                <Box component="span" sx={{ fontWeight: 600 }}>
                                    Tytuł:
                                </Box>
                            </Typography>
                            <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                                {protocolDocument.title || "-"}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                <Box
                    sx={(t) => ({
                        position: "relative",
                        border: `1px solid ${t.palette.divider}`,
                        borderRadius: 1.5,
                        p: 2,
                        pt: 3,
                        bgcolor: t.palette.background.paper,
                    })}
                    aria-labelledby="report-data-legend"
                >
                    <Typography
                        id="report-data-legend"
                        variant="overline"
                        sx={(t) => ({
                            position: "absolute",
                            top: 0,
                            left: 16,
                            transform: "translateY(-50%)",
                            px: 1,
                            fontWeight: 700,
                            bgcolor: t.palette.background.paper,
                        })}
                    >
                        Parametry raportu
                    </Typography>

                    <Stack spacing={2}>
                        <DocumentNumberTextField
                            fullWidth={true}
                            required={true}
                            value={documentNumber}
                            onChange={handleDocumentNumberChange}
                            label={<Localized id="document-number"><span>Numer dokumentu</span></Localized>}
                            documentType={protocolDocument.type}
                        />

                        <TextField
                            label="Tytuł dokumentu"
                            value={documentTitle}
                            onChange={(e) => setDocumentTitle(e.target.value)}
                            required
                            fullWidth
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={copyTeamFromProtocol}
                                    onChange={(e) => {
                                        const v = e.target.checked;
                                        setCopyTeamFromProtocol(v);
                                        if (v) { setResponsiblePerson(null); setResponsibleDirty(false); }
                                    }}
                                />
                            }
                            label="Skopiuj zespół z protokołu"
                        />

                        <LoadUsers component={<Skeleton variant="rectangular" height={180} />}>
                            <Autocomplete
                                options={allUsers}
                                onChange={(_, u) => {
                                    setResponsiblePerson(u ?? null);
                                    setResponsibleDirty(true);
                                }}
                                onBlur={() => setResponsibleDirty(true)}
                                value={responsiblePerson}
                                disabled={copyTeamFromProtocol}

                                isOptionEqualToValue={(o, v) => o.userName === v.userName}
                                getOptionLabel={(u) => (u ? `${u.firstname} ${u.surname} (${u.email})` : "")}
                                renderOption={(props, u) => (
                                    <li {...props} key={u.userName}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Avatar
                                                sx={{ width: 28, height: 28, fontSize: 12, lineHeight: 1 }}
                                                src={(u as any).avatarUrl}
                                            >
                                                {<PersonIcon sx={{ fontSize: 16 }} />}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {u.firstname} {u.surname}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {u.email}{u.position ? ` · ${u.position}` : ""}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField {...params}
                                        label="Osoba odpowiedzialna za przygotowanie dokumentu"
                                        placeholder="Wybierz osobę"
                                        required={!copyTeamFromProtocol}
                                        error={responsibleDirty && !isResponsibleOk}
                                        helperText={responsibleDirty && !isResponsibleOk ? <RequiredError /> : null} />
                                )}
                            />
                        </LoadUsers>
                    </Stack>
                </Box>


                {serverError && (
                    <Alert
                        sx={{ mt: 2 }}
                        onClose={() => setServerError("")}
                        severity="error"
                    >
                        <Localized id={serverError}>
                            <span>Server error</span>
                        </Localized>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2, justifyContent: "flex-end" }}>
                <GenerateButton loading={saving} isDisabled={!isValid} onClick={handleConfirmGenerateClick} />
                <CancelButton loading={saving} onClick={onCancel} />
            </DialogActions>
        </Dialog>
    );
};
